// frontend/client/src/components/MediaLibrary.js
// MERGED: Kimi's visual redesign (card language, hover overlay, empty state, toolbar)
// grafted onto the REAL useMediaLibrary() hook and REAL backend contract —
// Kimi's own MediaLibrary.js invented endpoints (/media/my-assets) and skipped
// POST /api/video/register, which would have broken Edit-in-VideoTools for any
// video uploaded through it. This version keeps the real data layer intact.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Image as ImageIcon, Film, Trash2, Loader2,
  Copy, Video as VideoIcon, Wand2, Upload, Plus, FileVideo
} from 'lucide-react';
import { useMediaLibrary } from '../hooks/useMediaLibrary';
import { useToast } from '../hooks/useToast';
import { useMinoStore } from '../store/useMinoStore';
import MediaPreview from './MediaPreview';

const FILTER_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'video', label: 'Videos' },
  { id: 'image', label: 'Images' },
];

const ML_CSS = `
  @keyframes ml-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  .ml-spin { animation: ml-spin 1s linear infinite; }

  .ml-root { display: flex; flex-direction: column; gap: 20px; padding: 24px; font-family: var(--font-body); background: var(--bg-canvas); min-height: 100%; }
  .ml-page-header { display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 4px; }
  .ml-page-title { font-family: var(--font-display); font-size: 22px; font-weight: 700; color: var(--text-main); margin: 0 0 3px; letter-spacing: -0.02em; }
  .ml-page-sub { font-size: 13px; color: var(--text-muted); margin: 0; }
  .ml-upload-btn { display: inline-flex; align-items: center; gap: 6px; padding: 10px 18px; border-radius: 10px; background: linear-gradient(135deg, #4F46E5, #8B5CF6); color: white; font-size: 13px; font-weight: 700; border: none; cursor: pointer; box-shadow: var(--shadow-brand); transition: opacity 0.2s; }
  .ml-upload-btn:hover { opacity: 0.9; }

  .ml-toolbar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 8px; }
  .ml-search-wrap { position: relative; flex: 1; max-width: 320px; }
  .ml-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); pointer-events: none; }
  .ml-search { width: 100%; padding: 9px 12px 9px 36px; border-radius: 10px; border: 1px solid var(--border-light); background: var(--bg-surface); font-size: 13px; font-family: var(--font-body); color: var(--text-main); outline: none; transition: all 0.15s; box-sizing: border-box; }
  .ml-search:focus { border-color: var(--brand-primary); box-shadow: 0 0 0 3px rgba(124,58,237,0.1); }
  .ml-filter-btn { padding: 8px 14px; border-radius: 10px; border: 1px solid var(--border-light); background: var(--bg-surface); font-size: 12px; font-weight: 700; color: var(--text-main); cursor: pointer; transition: all 0.15s; }
  .ml-filter-btn:hover { border-color: var(--brand-border); }
  .ml-filter-btn.active { background: var(--brand-secondary); color: var(--brand-secondary-contrast, var(--brand-primary)); border-color: var(--brand-border); }

  .ml-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 16px; }
  .ml-tile { position: relative; aspect-ratio: 1; border-radius: 16px; overflow: hidden; background: #111; border: 1px solid var(--border-light); cursor: pointer; transition: all 0.2s; box-shadow: var(--shadow-sm); }
  .ml-tile:hover { transform: translateY(-2px); box-shadow: var(--shadow-lg); border-color: var(--brand-border); }
  .ml-tile-media { width: 100%; height: 100%; object-fit: cover; }
  .ml-tile-overlay { position: absolute; inset: 0; background: rgba(0,0,0,0.6); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; opacity: 0; transition: opacity 0.2s; padding: 12px; backdrop-filter: blur(4px); }
  .ml-tile:hover .ml-tile-overlay { opacity: 1; }
  .ml-overlay-btn { display: flex; align-items: center; gap: 6px; padding: 7px 12px; border-radius: 8px; font-size: 11px; font-weight: 700; cursor: pointer; border: 1px solid rgba(255,255,255,0.2); width: 100%; justify-content: center; transition: all 0.15s; background: rgba(255,255,255,0.15); color: white; backdrop-filter: blur(8px); }
  .ml-overlay-btn:hover { background: rgba(255,255,255,0.25); }
  .ml-overlay-btn.danger:hover { background: rgba(239,68,68,0.3); border-color: rgba(239,68,68,0.5); }
  .ml-tile-badge { position: absolute; top: 10px; left: 10px; padding: 3px 8px; border-radius: 6px; background: rgba(0,0,0,0.6); color: white; font-size: 10px; font-weight: 700; text-transform: uppercase; display: flex; align-items: center; gap: 4px; backdrop-filter: blur(4px); }

  .ml-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 24px; text-align: center; background: var(--bg-surface); border: 1px dashed var(--border-light); border-radius: 16px; }
  .ml-empty-icon { width: 64px; height: 64px; border-radius: 16px; background: var(--brand-secondary); display: flex; align-items: center; justify-content: center; margin-bottom: 16px; }
  .ml-empty-title { font-family: var(--font-display); font-size: 16px; font-weight: 700; color: var(--text-main); margin: 0 0 6px; }
  .ml-empty-sub { font-size: 13px; color: var(--text-muted); margin: 0 0 20px; }

  .ml-skeleton { background: var(--bg-canvas); border-radius: 16px; animation: ml-pulse 1.5s ease-in-out infinite; aspect-ratio: 1; border: 1px solid var(--border-light); }
  @keyframes ml-pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
  @media (max-width: 640px) { .ml-grid { grid-template-columns: repeat(2, 1fr); } }
`;

export default function MediaLibrary() {
  const { showToast } = useToast();
  const addToast = ({ message, type = 'success' } = {}) => showToast(message, type);
  const navigate = useNavigate();

  // REAL hook — hits /api/media/library (Cloudinary) + /api/video/my-videos
  // (Mongo), dedupes, and returns native Cloudinary-shaped items
  // (public_id, secure_url, resource_type). Do not swap this for a custom
  // fetch — it already handles the two-source merge correctly.
  const { items, loading, searchTerm, setSearchTerm, filterType, setFilterType, deleteMedia, refetchMedia } = useMediaLibrary();
  const triggerMino = useMinoStore((s) => s.trigger);

  const [previewMedia, setPreviewMedia] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const openPreview = (item) => { setPreviewMedia(item); setIsPreviewOpen(true); };

  const handleDelete = async (e, publicId, type) => {
    e.stopPropagation();
    if (!window.confirm('Delete this asset? This cannot be undone.')) return;
    setDeletingId(publicId);
    try {
      await deleteMedia(publicId, type);
      addToast({ type: 'success', message: 'Asset deleted' });
    } catch {
      addToast({ type: 'error', message: 'Delete failed' });
      triggerMino('error', "Delete didn't go through", 'Give it another try in a moment');
      refetchMedia();
    }
    setDeletingId(null);
  };

  const copyUrl = (e, url) => {
    e.stopPropagation();
    navigator.clipboard.writeText(url);
    addToast({ type: 'success', message: 'URL copied!' });
  };

  // Real handoff to Video Tools — preserves the shape VideoTools.js/videoRoutes.js
  // actually expect. If this item is already a Mongo-registered video (has a
  // real _id from /video/my-videos), pass that through so tool calls work
  // immediately. If it's a Cloudinary-only asset not yet registered, VideoTools.js
  // needs to register it first before any tool can run against it.
  const sendToVideoTools = (e, item) => {
    e.stopPropagation();
    navigate('/dashboard/video-tools', {
      state: {
        preloadedMedia: {
          _id: item._id || null, // null if not yet registered in Mongo
          name: item.public_id?.split('/').pop() || item.name || 'Media asset',
          status: item.status || 'ready',
          createdAt: item.created_at || item.createdAt || new Date().toISOString(),
          originalVideo: {
            secure_url: item.secure_url,
            publicId: item.public_id,
          },
          processedUrl: item.processedUrl || null,
          captionedVideoUrl: item.captionedVideoUrl || null,
        }
      }
    });
  };

  const sendToContentLab = (e, item) => {
    e.stopPropagation();
    const isVideo = item.resource_type === 'video';
    navigate('/dashboard/content-lab', {
      state: isVideo
        ? { video: { _id: item._id || item.public_id, originalVideo: { secure_url: item.secure_url, publicId: item.public_id } } }
        : { imageUrl: item.secure_url }
    });
  };

  return (
    <div className="ml-root">
      <style>{ML_CSS}</style>

      <div className="ml-page-header">
        <div>
          <h1 className="ml-page-title">Media Library</h1>
          <p className="ml-page-sub">All your images and videos in one place</p>
        </div>
        <button className="ml-upload-btn" onClick={() => navigate('/dashboard/video-tools')}>
          <VideoIcon size={14} /> Upload Video
        </button>
      </div>

      <div className="ml-toolbar">
        <div className="ml-search-wrap">
          <Search size={14} color="var(--text-muted)" className="ml-search-icon" />
          <input
            className="ml-search"
            placeholder="Search assets..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        {FILTER_OPTIONS.map(f => (
          <button
            key={f.id}
            className={`ml-filter-btn ${filterType === f.id ? 'active' : ''}`}
            onClick={() => setFilterType(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="ml-grid">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="ml-skeleton" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="ml-empty">
          <div className="ml-empty-icon">
            <FileVideo size={28} color="var(--brand-primary)" />
          </div>
          <p className="ml-empty-title">No assets yet</p>
          <p className="ml-empty-sub">Upload a video to get started — it'll appear in your Content Pipeline.</p>
          <button className="ml-upload-btn" onClick={() => navigate('/dashboard/video-tools')}>
            <Upload size={14} /> Upload your first video
          </button>
        </div>
      ) : (
        <div className="ml-grid">
          {items.map((item, idx) => {
            const isVideo = item.resource_type === 'video';
            const mediaUrl = item.secure_url;
            const thumbUrl = isVideo && mediaUrl?.includes('cloudinary')
              ? mediaUrl.replace(/\.(mp4|mov|webm)$/i, '.jpg')
              : mediaUrl;
            const itemId = item.public_id || item._id;
            const isDeleting = deletingId === itemId;

            return (
              <div key={itemId || idx} className="ml-tile" onClick={() => openPreview(item)}>
                {isVideo ? (
                  <>
                    <img src={thumbUrl} alt="" className="ml-tile-media" style={{ opacity: 0.85 }} loading="lazy" onError={e => { e.target.style.display = 'none'; }} />
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                      <div style={{ background: 'rgba(0,0,0,0.5)', borderRadius: '50%', padding: 12 }}>
                        <Film size={22} color="white" />
                      </div>
                    </div>
                  </>
                ) : (
                  <img src={mediaUrl} alt="" className="ml-tile-media" loading="lazy" />
                )}

                <span className="ml-tile-badge">
                  {isVideo ? <Film size={9} /> : <ImageIcon size={9} />}
                  {isVideo ? 'video' : 'image'}
                </span>

                <div className="ml-tile-overlay">
                  {isVideo && (
                    <button className="ml-overlay-btn" onClick={e => sendToVideoTools(e, item)}>
                      <Wand2 size={12} /> Edit in Video Tools
                    </button>
                  )}
                  <button className="ml-overlay-btn" onClick={e => sendToContentLab(e, item)}>
                    <Wand2 size={12} /> Generate Caption
                  </button>
                  <button className="ml-overlay-btn" onClick={e => copyUrl(e, mediaUrl)}>
                    <Copy size={12} /> Copy URL
                  </button>
                  <button
                    className="ml-overlay-btn danger"
                    onClick={e => handleDelete(e, itemId, isVideo ? 'video' : 'image')}
                    disabled={isDeleting}
                  >
                    {isDeleting ? <Loader2 size={11} className="ml-spin" /> : <Trash2 size={12} />}
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isPreviewOpen && previewMedia && (
        <MediaPreview media={previewMedia} isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} />
      )}
    </div>
  );
}